import { SectionsTreeNode } from '../types';
import { recalculateNumberOfPages } from './recalculateNumberOfPages';

/**
 * Recursively removes nodes with specified ids and returns a new tree.
 */
const removeNodesById = (nodes: SectionsTreeNode[], idsToRemove: Set<string>): SectionsTreeNode[] =>
  nodes
    .filter((node) => !idsToRemove.has(node.id))
    .map((node) => ({
      ...node,
      children: node.children ? removeNodesById(node.children, idsToRemove) : undefined
    }));

/**
 * Recursively searches for a node by id.
 */
const findNodeById = (nodes: SectionsTreeNode[], id: string): SectionsTreeNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children) {
      const result = findNodeById(node.children, id);
      if (result) return result;
    }
  }
  return null;
};

/**
 * Recursively checks if the target node is a descendant(children) of the source node.
 */
const isDescendant = (source: SectionsTreeNode, targetId: string): boolean => {
  if (!source.children) return false;
  return source.children.some((child) => child.id === targetId || isDescendant(child, targetId));
};

/**
 * Correct index if nodes are moved within the same parent
 */
const adjustedIndexForSameParent = (parent: SectionsTreeNode, index: number, dragIds: Set<string>): number => {
  if (!parent.children) return index;
  const beforeIndex = parent.children.slice(0, index);
  const count = beforeIndex.filter((child) => dragIds.has(child.id)).length;
  return index - count;
};

/**
 * Recursively inserts nodes under the given parentId at a specific index.
 */
const insertNodesById = (
  nodes: SectionsTreeNode[],
  parentId: string,
  index: number,
  newNodes: SectionsTreeNode[]
): SectionsTreeNode[] =>
  nodes.map((node) => {
    if (node.id === parentId) {
      const children = node.children ? [...node.children] : [];
      const updatedChildren = [...children.slice(0, index), ...newNodes, ...children.slice(index)];
      return { ...node, children: updatedChildren };
    }
    return {
      ...node,
      children: node.children ? insertNodesById(node.children, parentId, index, newNodes) : undefined
    };
  });

interface MoveNodesOptions {
  tree: SectionsTreeNode[];
  dragNodes: SectionsTreeNode[];
  dragIds: string[];
  parentNode: SectionsTreeNode;
  parentId: string;
  index: number;
}

/**
 * Moves dragNodes to the specified position within parentNode.children.
 */
export const moveNodesInTree = ({
  tree,
  dragNodes,
  dragIds,
  parentNode,
  parentId,
  index
}: MoveNodesOptions): SectionsTreeNode[] => {
  const idsToRemove = new Set(dragIds);
  const treeWithoutDragged = removeNodesById(tree, idsToRemove);

  const targetParent = findNodeById(treeWithoutDragged, parentId);
  if (!targetParent) {
    throw new Error(`Parent node with id "${parentId}" not found.`);
  }

  dragNodes.forEach((dragNode) => {
    if (isDescendant(dragNode, parentId)) {
      throw new Error(`Cannot move a node into its own descendant.`);
    }
  });

  // Correct index if nodes are moved within the same parent
  const correctedIndex = adjustedIndexForSameParent(parentNode, index, idsToRemove);

  const treeWithInserted = insertNodesById(treeWithoutDragged, parentId, correctedIndex, dragNodes);

  // Recalculate numberOfPages for all nodes in the tree after move
  const recalculatedTree = recalculateNumberOfPages(treeWithInserted);
  return recalculatedTree;
};
